import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const FoundingCircleSection = () => {
  const { language } = useLanguage();
  
  const founders = [
    {
      id: 1,
      name: 'Rick Rock',
      role: 'Rick Rock Ventures & Shaping X Consultancy',
      image: 'https://customer-assets.emergentagent.com/job_f9b25d35-d6a1-40d8-b060-8e23681768e1/artifacts/k736mowk_Rick%20foto.png',
      hasImage: true,
      linkedin: 'https://www.linkedin.com/in/rickrockventures/',
      whatsapp: 'https://wa.me/15175056943',
    },
    {
      id: 2,
      name: 'Lena Kwasow',
      role: language === 'de' ? 'the soulful architect' : 'the soulful architect',
      image: 'https://customer-assets.emergentagent.com/job_f9b25d35-d6a1-40d8-b060-8e23681768e1/artifacts/hcy6ez75_Bildschirmfoto%202026-02-05%20um%2011.17.46.png',
      hasImage: true,
      linkedin: null,
      whatsapp: null,
    },
  ];
  
  return (
    <section 
      id="founding-circle"
      className="relative py-32 bg-[#030303] overflow-hidden"
      data-testid="founding-circle-section"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#050505] to-[#030303]" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
        {/* Section Title */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
        >
          <motion.p
            className="text-xs tracking-[0.4em] uppercase text-[#2bfecd]/60 mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            {language === 'de' ? 'Die Visionäre' : 'The Visionaries'}
          </motion.p>
          
          <motion.h2 
            className="font-cinzel text-4xl md:text-5xl lg:text-6xl tracking-[0.15em] uppercase text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.3 }}
          >
            Founding Team
          </motion.h2>
          
          <motion.div 
            className="w-24 h-px bg-gradient-to-r from-transparent via-[#2bfecd]/40 to-transparent mx-auto"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          />
        </motion.div>
        
        {/* Founders Grid */}
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 max-w-4xl mx-auto">
          {founders.map((founder, index) => (
            <motion.div
              key={founder.id}
              className="text-center"
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: index * 0.2 }}
            >
              {/* Photo Container */}
              <div className="relative w-48 h-48 md:w-56 md:h-56 mx-auto mb-8">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-[#2bfecd]/10 rounded-full blur-2xl" />
                
                {/* Photo Frame */}
                <div className="relative w-full h-full rounded-full overflow-hidden border border-white/10 bg-[#0a0a0a]">
                  {founder.hasImage ? (
                    <img 
                      src={founder.image} 
                      alt={founder.name}
                      className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                    />
                  ) : (
                    // Placeholder for Lena
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] to-[#151515]">
                      <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-3 rounded-full border border-[#2bfecd]/30 flex items-center justify-center">
                          <span className="text-2xl text-[#2bfecd]/50 font-cinzel">LK</span>
                        </div>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-white/30">
                          {language === 'de' ? 'Foto folgt' : 'Photo coming'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Corner Accents */}
                <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-[#2bfecd]/20 rounded-tl-full" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-[#2bfecd]/20 rounded-br-full" />
              </div>
              
              {/* Name */}
              <motion.h3 
                className="font-cinzel text-xl md:text-2xl tracking-[0.1em] uppercase text-white mb-3"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.2 }}
              >
                {founder.name}
              </motion.h3>
              
              {/* Role */}
              <motion.p 
                className="text-xs tracking-[0.2em] uppercase text-[#2bfecd]/70 mb-4"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 + index * 0.2 }}
              >
                {founder.role}
              </motion.p>
              
              {/* Social Links */}
              {(founder.linkedin || founder.whatsapp) && (
                <motion.div 
                  className="flex items-center justify-center gap-4"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.5 + index * 0.2 }}
                >
                  {founder.linkedin && (
                    <a 
                      href={founder.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-[#2bfecd] hover:border-[#2bfecd]/30 transition-all duration-300"
                      aria-label="LinkedIn"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {founder.whatsapp && (
                    <a 
                      href={founder.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-[#2bfecd] hover:border-[#2bfecd]/30 transition-all duration-300"
                      aria-label="WhatsApp"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
        
        {/* Decorative Line */}
        <motion.div 
          className="flex items-center justify-center gap-6 mt-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.6 }}
        >
          <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#2bfecd]/20" />
          <div className="w-2 h-2 rotate-45 border border-[#2bfecd]/30" />
          <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#2bfecd]/20" />
        </motion.div>
      </div>
    </section>
  );
};

export default FoundingCircleSection;
